import React, { useState, useEffect } from 'react';
import { formatDateTime, initializeFormats, getFormatConfig } from '../../../utils/global';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
  AlertTitle,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import { DataGridPro } from '@mui/x-data-grid-pro';
import { useToast } from '../../hooks/useToast';
import { useUserRights } from '../../../context/UserRightsContext';
import TreasuryFileUploadDialog from './TreasuryFileUploadDialog';
import ExcelViewerDialog from './ExcelViewerDialog';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogCloseIconSx,
  harmonateGridActionIconSx,
  harmonatePlainIconButtonSx,
} from '../../../theme/harmonateStyles';

// Utility: Get tenant_id from localStorage
const getTenantId = () => localStorage.getItem('tenant_id');

const Treasury = () => {
  const [dateTimeFormat, setDateTimeFormat] = useState('DD/MM/YYYY hh:mm:ss a');
  const [treasuryData, setTreasuryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('TR');
  const [categoryAnchorEl, setCategoryAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [excelViewerOpen, setExcelViewerOpen] = useState(false);
  const [selectedFileInstanceId, setSelectedFileInstanceId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  // Audit logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [documentDetails, setDocumentDetails] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const { showSuccess, showError } = useToast();
  const { hasPermission } = useUserRights();

  // Load treasury data and initialize formats on mount
  useEffect(() => {
    const tenantId = localStorage.getItem('tenant_id');
    if (tenantId) {
      initializeFormats(tenantId).then(() => {
        const config = getFormatConfig();
        if (config.DateTimeFormat) {
          setDateTimeFormat(config.DateTimeFormat);
        }
      });
    }
    fetchTreasuryData();
  }, []);

  // Fetch treasury data and file instances
  const fetchTreasuryData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const tenantId = getTenantId();
      if (!token || !tenantId) throw new Error('Missing token or tenant ID');

      // First, fetch the document types for the TR category
      const docTypesUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/document-types/by-category/TR`;

      const docTypesResponse = await fetch(docTypesUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!docTypesResponse.ok) throw new Error(`Failed to fetch document types: ${docTypesResponse.status}`);

      const docTypes = await docTypesResponse.json();

      if (!docTypes || docTypes.length === 0) {
        throw new Error('No document types found for TR category');
      }

      // Take the first document type (assuming there's only one for TR)
      const docType = docTypes[0];
      const docTypeId = docType.id;

      if (!docTypeId) {
        throw new Error('No document type ID found');
      }

      // Now fetch file instances for this document type
      const fileInstancesUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/by-document-type/${docTypeId}?tenantId=${tenantId}`;
      
      const fileInstancesResponse = await fetch(fileInstancesUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!fileInstancesResponse.ok) {
        throw new Error(`Failed to fetch file instances: ${fileInstancesResponse.status}`);
      }

      let fileInstances = await fileInstancesResponse.json();
      
      // Sort file instances by createdAt in descending order (newest first)
      fileInstances = fileInstances.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA; // Sort in descending order (newest first)
      });
      
      // Create the data structure with document type and file instances
      const combinedData = {
        ...docType,
        fileInstances: fileInstances || []
      };

      setTreasuryData([combinedData]);
      showSuccess('Treasury data loaded successfully');
    } catch (err) {
      console.error('Error fetching treasury data:', err);
      setError(err.message || 'Failed to load data');
      showError('Failed to load treasury data');
    } finally {
      setLoading(false);
    }
  };

  // Handle info click - fetch audit logs
  const handleInfoClick = async (event, row) => {
    event.stopPropagation();
    try {
      setLogsLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = getTenantId();
      
      if (!token || !tenantId) {
        throw new Error('Authentication required');
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${row.id}/with-audit-logs?tenantId=${tenantId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch audit logs: ${response.status}`);
      }
      
      const data = await response.json();
      setDocumentDetails(data.document);
      setAuditLogs(data.auditLogs || []);
      setActiveTab(0);
      setIsLogsModalOpen(true);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      showError('Failed to load document details and audit logs');
    } finally {
      setLogsLoading(false);
    }
  };

  // Handlers
  const navigate = useNavigate();
  
  const handleRowClick = (params) => {
    // Only handle wire transfer navigation
    if (params.row.unitOfWorkId) {
      if (params.row.processId) {
        navigate(`/treasury/wire-transfer/${params.row.processId}`);
      } else {
        console.error('Missing required processId for wire transfer viewer');
        setError('Cannot open file: Missing required file information');
      }
    }
  };

  const handleUploadDialogOpen = () => setOpenUploadDialog(true);
  const handleUploadDialogClose = () => {
    setOpenUploadDialog(false);
  };

  const handleCloseExcelViewer = () => {
    setExcelViewerOpen(false);
    setSelectedRow(null);
  };

  const handleUploadSuccess = () => {
    handleUploadDialogClose();
    fetchTreasuryData();
    showSuccess('File uploaded successfully');
  };

  const handleRefresh = () => fetchTreasuryData();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Delete handlers
  const handleDeleteClick = (event, row) => {
    event.stopPropagation();
    setFileToDelete(row);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteConfirmOpen(false);
    setFileToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete?.id) {
      showError('File ID not found');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const deleteUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileToDelete.id}/delete`;
      const response = await fetch(deleteUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete file: ${response.status} - ${errorText}`);
      }

      showSuccess('File deleted successfully');
      handleDeleteClose();
      fetchTreasuryData();
    } catch (error) {
      console.error('Error during file deletion:', error);
      showError(`Failed to delete file: ${error.message}`);
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'File Name',
      flex: 2,
      minWidth: 200,
      valueGetter: (params) => params.row.name || 'N/A',
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '0.875rem' }}>
          {params.row.createdAt ? formatDateTime(params.row.createdAt, dateTimeFormat) : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'sourceGeneralLedger',
      headerName: 'Source GL',
      flex: 1.5,
      minWidth: 150,
      valueGetter: (params) => params.row.sourceGeneralLedger || 'N/A',
    },
    {
      field: 'username',
      headerName: 'Uploaded By',
      flex: 1.5,
      minWidth: 150,
      valueGetter: (params) => params.row.username || 'N/A',
    },
    {
      field: 'processStep',
      headerName: 'Process Step',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.processStep || 'N/A',
    },
    // Preview Icon Column
    {
      field: 'preview',
      headerName: '',
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => '',
      renderCell: (params) => {
        const fileName = params.row.name?.toLowerCase() || '';
        const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
        
        if (!isExcel) return null; // Only show icon for Excel files
        
        return (
          <Tooltip title="Preview Excel file">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation(); // Prevent row click
                setSelectedFileInstanceId(params.row.id);
                setExcelViewerOpen(true);
              }}
              sx={harmonateGridActionIconSx}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => '',
      renderCell: (params) => (
        <Tooltip title="View details">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleInfoClick(e, params.row);
            }}
            sx={harmonateGridActionIconSx}
          >
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: 'delete',
      headerName: '',
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => '',
      renderCell: (params) => (
        <Tooltip title="Delete file">
          <IconButton
            size="small"
            onClick={(e) => handleDeleteClick(e, params.row)}
            sx={{
              ...harmonateGridActionIconSx,
              '&:hover': {
                bgcolor: 'rgba(239, 63, 63, 0.08)',
                color: ht.red,
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // Early loading state
  if (loading && treasuryData.length === 0) {
    return (
      <Box sx={{ ...harmonatePageSx, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: ht.white }} />
      </Box>
    );
  }

  return (
    <Box sx={{ ...harmonatePageSx, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1" sx={harmonatePageTitleSx}>
          Treasury
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {hasPermission('Treasury', 'upload') && (
            <>
              <Button
                variant="contained"
                onClick={handleUploadDialogOpen}
                disabled={loading}
                sx={harmonateProceedButtonSx}
              >
                File Upload
              </Button>
              <IconButton 
                onClick={handleRefresh} 
                disabled={loading}
                sx={harmonatePlainIconButtonSx}
              >
                <RefreshIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {/* Upload Dialog */}
      <TreasuryFileUploadDialog
        open={openUploadDialog}
        onClose={handleUploadDialogClose}
        onUploadComplete={handleUploadSuccess}
      />
      
      <ExcelViewerDialog
        open={excelViewerOpen}
        onClose={handleCloseExcelViewer}
        fileInstanceId={selectedFileInstanceId}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Data Table */}
      <Paper sx={{ 
        ...harmonateCardSx,
        bgcolor: ht.white,
        display: 'flex', 
        flexDirection: 'column',
        width: '100%',
        flex: 1,
        minHeight: 0,
        mt: 2,
        overflow: 'hidden'
      }}>
        {treasuryData.length > 0 && treasuryData[0].fileInstances ? (
          <DataGridPro
            rows={treasuryData[0].fileInstances}
            getRowId={(row) => row.id || Math.random().toString(36).substr(2, 9)}
            columns={columns}
            columnResizeMode="onEnd"
            resizable
            onRowClick={handleRowClick}
            pagination
            rowsPerPageOptions={[10, 25, 50]}
            pageSize={10}
            autoPageSize={false}
            sx={{
              ...harmonateDataGridSx,
              flex: 1,
              '& .MuiDataGrid-row': {
                ...harmonateDataGridSx['& .MuiDataGrid-row'],
                cursor: 'pointer',
              },
            }}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 25 },
              },
            }}
            disableRowSelectionOnClick
          />
        ) : (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            p: 3 
          }}>
            <Typography variant="body1" color="textSecondary">
              {loading ? 'Loading file instances...' : 'No file instances found'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteClose}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Delete File</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{fileToDelete?.name || 'this file'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleDeleteClose} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleDeleteConfirm} sx={harmonateAbortButtonSx}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Details & Audit Logs Modal */}
      {isLogsModalOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1300,
          }}
          onClick={() => {
            setIsLogsModalOpen(false);
            setActiveTab(0);
          }}
        >
          <Paper 
            onClick={(e) => e.stopPropagation()}
            sx={{ 
              width: '95%', 
              maxWidth: '1200px',
              height: '90vh',
              display: 'flex',
              flexDirection: 'column',
              p: 0,
              overflow: 'hidden',
              borderRadius: ht.cardRadius,
              boxShadow: ht.cardShadow,
            }}
          >
            <Box 
              sx={{ 
                ...harmonateDialogTitleSx,
                p: 2,
                borderBottom: 'none',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 500, color: ht.white }}>
                Document Details & Audit Logs
              </Typography>
              <IconButton 
                size="small" 
                onClick={() => {
                  setIsLogsModalOpen(false);
                  setActiveTab(0);
                }}
                sx={harmonateDialogCloseIconSx}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            
            {logsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress size={24} sx={{ color: ht.blue }} />
              </Box>
            ) : (
              <Box sx={{ p: 2, overflow: 'auto', flex: 1 }}>
                {/* Document Details Section */}
                {documentDetails && (
                  <Box sx={{ 
                    mb: 3, 
                    p: 2.5, 
                    bgcolor: ht.listBg, 
                    borderRadius: ht.cardRadius,
                    border: `1px solid ${ht.line}`,
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 2,
                      pb: 1.5,
                      borderBottom: `1px solid ${ht.line}`,
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text, letterSpacing: 0.4 }}>
                        DOCUMENT INFORMATION
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                      gap: 3,
                      rowGap: 2.5,
                      '& > div': {
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                      }
                    }}>
                      <div>
                        <Typography variant="caption" color="textSecondary" display="block">Document Name</Typography>
                        <Typography variant="body2" noWrap>{documentDetails.name || 'N/A'}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption" color="textSecondary" display="block">Document Type</Typography>
                        <Typography variant="body2">{documentDetails.documentTypeId || 'N/A'}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption" color="textSecondary" display="block">Uploaded At</Typography>
                        <Typography variant="body2">
                          {documentDetails.uploadAt ? new Date(documentDetails.uploadAt).toLocaleString() : 'N/A'}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="caption" color="textSecondary" display="block">Location</Typography>
                        <Tooltip 
                          title={documentDetails.location || 'N/A'}
                          arrow
                          placement="top"
                        >
                          <Typography 
                            variant="body2" 
                            noWrap
                            sx={{
                              maxWidth: '300px',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              display: 'inline-block',
                              verticalAlign: 'bottom',
                              fontSize: '0.875rem',
                              color: 'text.primary',
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                                textDecorationColor: 'text.secondary',
                                textDecorationThickness: '1px',
                                textUnderlineOffset: '2px',
                              }
                            }}
                          >
                            {documentDetails.location || 'N/A'}
                          </Typography>
                        </Tooltip>
                      </div>
                      <div>
                        <Typography variant="caption" color="textSecondary" display="block">Unit of Work</Typography>
                        <Typography variant="body2">
                          {documentDetails.unitOfWorkId || 'N/A'}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="caption" color="textSecondary" display="block">Integration ID</Typography>
                        <Typography variant="body2" noWrap>{documentDetails.integrationId || 'N/A'}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption" color="textSecondary" display="block">Source General Ledger</Typography>
                        <Typography variant="body2" noWrap>{documentDetails.sourceGeneralLedger || 'N/A'}</Typography>
                      </div>
                    </Box>
                  </Box>
                )}

                {/* Audit Logs Section */}
                <Box>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 1.5,
                    pb: 1,
                    borderBottom: `1px solid ${ht.line}`,
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text, letterSpacing: 0.4 }}>
                      AUDIT LOGS
                    </Typography>
                  </Box>
                  {auditLogs.length > 0 ? (
                    <Box sx={{ height: 'calc(100% - 40px)', width: '100%' }}>
                      <DataGridPro
                        rows={auditLogs}
                        columnResizeMode="onEnd"
                        resizable
                        pagination
                        rowsPerPageOptions={[10, 25, 50]}
                        pageSize={10}
                        autoPageSize={false}
                        columns={[
                          { 
                            field: 'createdAt', 
                            headerName: 'Timestamp', 
                            flex: 1,
                            minWidth: 160,
                            valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : 'N/A',
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'black' }}>
                                Timestamp
                              </Typography>
                            )
                          },
                          { 
                            field: 'service', 
                            headerName: 'Service', 
                            flex: 0.8,
                            minWidth: 150,
                            cellClassName: 'service-cell',
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'black' }}>
                                Service
                              </Typography>
                            ),
                            renderCell: (params) => (
                              <Box sx={{ 
                                bgcolor: params.value === 'upload' ? 'rgba(25, 118, 210, 0.12)' : 
                                        params.value === 'document_classification' ? 'rgba(46, 125, 50, 0.12)' :
                                        params.value === 'extraction' ? 'rgba(255, 152, 0, 0.12)' :
                                        params.value === 'ingest' ? 'rgba(156, 39, 176, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                                color: params.value === 'upload' ? 'primary.main' : 
                                       params.value === 'document_classification' ? 'success.dark' :
                                       params.value === 'extraction' ? 'warning.dark' :
                                       params.value === 'ingest' ? 'secondary.main' : 'text.secondary',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 4,
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                minWidth: 'fit-content',
                                width: 'auto',
                                whiteSpace: 'nowrap',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {params.value || 'N/A'}
                              </Box>
                            )
                          },
                          { 
                            field: 'action', 
                            headerName: 'Action', 
                            flex: 1,
                            minWidth: 120,
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'black' }}>
                                Action
                              </Typography>
                            ),
                            valueFormatter: (params) => params.value || 'N/A',
                            renderCell: (params) => (
                              <Box sx={{ 
                                color: 'black',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                fontFamily: 'monospace'
                              }}>
                                {params.formattedValue}
                              </Box>
                            )
                          },
                          { 
                            field: 'status', 
                            headerName: 'Status', 
                            flex: 0.8,
                            minWidth: 100,
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'black' }}>
                                Status
                              </Typography>
                            ),
                            renderCell: (params) => (
                              <Box sx={{ 
                                color: 'black',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                textTransform: 'uppercase'
                              }}>
                                {params.value || 'N/A'}
                              </Box>
                            )
                          },
                          { 
                            field: 'details', 
                            headerName: 'Details', 
                            flex: 2,
                            minWidth: 200,
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'black' }}>
                                Details
                              </Typography>
                            ),
                            renderCell: (params) => (
                              <Tooltip 
                                title={params.value ? 
                                  (typeof params.value === 'object' ? 
                                    JSON.stringify(params.value, null, 2) : 
                                    params.value.toString()) : 
                                  'N/A'} 
                                arrow
                                placement="top"
                              >
                                <Box sx={{ 
                                  width: '100%',
                                  '&:hover': {
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textDecorationColor: 'text.secondary',
                                    textDecorationThickness: '1px',
                                    textUnderlineOffset: '2px',
                                  }
                                }}>
                                  <Typography 
                                    variant="body2" 
                                    noWrap
                                    sx={{
                                      color: 'black',
                                      fontSize: '0.8rem',
                                      fontFamily: 'monospace',
                                    }}
                                  >
                                    {params.value ? 
                                      (typeof params.value === 'object' ? 
                                        JSON.stringify(params.value) : 
                                        params.value.toString()
                                      ).substring(0, 50) + (params.value.length > 50 ? '...' : '') : 
                                      'N/A'}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )
                          }
                        ]}
                        disableSelectionOnClick
                        disableColumnMenu
                        hideFooterSelectedRowCount
                        sx={{
                          ...harmonateDataGridSx,
                          '& .MuiDataGrid-columnHeaders': {
                            ...harmonateDataGridSx['& .MuiDataGrid-columnHeaders'],
                            minHeight: '40px !important',
                            maxHeight: '40px !important',
                          },
                          '& .MuiDataGrid-footerContainer': {
                            borderTop: `1px solid ${ht.line}`,
                            minHeight: '48px',
                          },
                        }}
                        components={{
                          LoadingOverlay: LinearProgress,
                          NoRowsOverlay: () => (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                              <Typography variant="body2" color="text.secondary">
                                No audit logs available
                              </Typography>
                            </Box>
                          ),
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography>No audit logs available</Typography>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default Treasury;